import { pushNotificationSettingsCollection } from '@/reducers/pushNotificationSettings'
import { useAppDispatch } from '@/hooks'
import { PushNotificationSettings } from '@/types/push_notification_settings'
import { useEffect, useState, useRef } from 'react'
import { isNativeMobile } from '@/authenticated/hooks/useIsMobile'
import {
  NOTIFICATION_PERMISSIONS_TYPE,
  postEventToMobileIfPresent,
  REQUEST_NOTIFICATION_PERMISSION_STATUS_ACTION,
} from '@/utils/messageUtils'

export default function usePushNotificationSettingsViewModel() {
  const isMobile = isNativeMobile()
  const dispatch = useAppDispatch()

  const [mobilePushEnabled, setMobilePushEnabled] = useState(true)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const {
    data: [pushNotificationSettings],
    isLoading,
  } = pushNotificationSettingsCollection.useFetch('push-notification-settings')

  const updateNotificationSetting = <K extends keyof PushNotificationSettings>(
    property: K,
    value: PushNotificationSettings[K],
  ) => {
    if (pushNotificationSettings) {
      dispatch(
        pushNotificationSettingsCollection.actions.update(
          'push-notification-settings',
          {
            [property]: value,
          },
          { optimistic: true },
        ),
      )
    }
  }

  const handleNotificationPermissions = (event: MessageEvent) => {
    if (event.data?.event?.type === NOTIFICATION_PERMISSIONS_TYPE) {
      setMobilePushEnabled(event.data?.event?.status === 'enabled')
    }
  }

  useEffect(() => {
    if (isMobile) {
      // Request the notification permission status from the mobile app
      // The mobile app will send back a message handled in handleNotificationPermissions
      postEventToMobileIfPresent(REQUEST_NOTIFICATION_PERMISSION_STATUS_ACTION)
      window.addEventListener('message', handleNotificationPermissions)

      // Set up polling to request notification permission status every second
      intervalRef.current = setInterval(() => {
        postEventToMobileIfPresent(
          REQUEST_NOTIFICATION_PERMISSION_STATUS_ACTION,
        )
      }, 2000)
    }

    // Cleanup function to clear the interval and remove event listener
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      window.removeEventListener('message', handleNotificationPermissions)
    }
  }, [isMobile])

  return {
    isMobile,
    mobilePushEnabled,
    pushNotificationSettings,
    isLoading,
    updateNotificationSetting,
  }
}
